import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { connect } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import withIntl from '../../../../components/Intl/WithIntl';
import { Row, Col, FormGroup, InputGroup, Label, Input, FormText, ButtonGroup, Button, ListGroup, ListGroupItem, Alert } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import Toggle from '../../../../components/Form/Toggle';

import { editConversationRequest, getConversationOption, getConversationTargetId, getConversationParticipantIds, getConversationParticipantId, CONVERSATION_STATUS_OPEN, CONVERSATION_STATUS_CLOSED } from '../../ConversationActions';
import { getLoggedUser, getUsers } from '../../../User/UserActions';
import { displayErrors } from '../../../Error/ErrorActions';

import Autocomplete from '../../../../components/Form/Autocomplete';

class ConversationConfig extends Component {
    constructor(props) {
        super(props);

        this.state = {
            conversation: props.conversation || { status: CONVERSATION_STATUS_OPEN },
            isSaving: false,
        };
    }

    handleChange = event => {
        const { target } = event;
        const value = target.type === 'checkbox' ? target.checked : target.value;
        const { name } = target;
        this.setState({
            conversation: {
                ...this.state.conversation,
                [name]: value,
            },
        });
    };

    handleChangeStatus = event => {
        this.setState({
            conversation: {
                ...this.state.conversation,
                status: event.target.checked ? CONVERSATION_STATUS_OPEN : CONVERSATION_STATUS_CLOSED,
            },
        });
    };

    handleChangeOption = event => {
        const { target } = event;
        const value = target.type === 'checkbox' ? target.checked : target.value;
        const { name } = target;
        this.setState({
            conversation: {
                ...this.state.conversation,
                options: {
                    ...this.state.conversation.options || {},
                    [name]: value,
                },
            },
        });
    };

    handleAddParticipant = newParticipant => {
        if(newParticipant) {
            this.setState({
                conversation: {
                    ...this.state.conversation,
                    participants: [
                        ...(this.state.conversation.participants || []),
                        newParticipant,
                    ],
                },
            }, () => Promise.resolve());
        }
    };

    handleRemoveParticipant = participant => {
        this.setState({
            conversation: {
                ...this.state.conversation,
                participants: (this.state.conversation.participants || []).filter(existingParticipant => getConversationParticipantId(existingParticipant) !== participant._id),
            },
        });
    };

    handleSubmit = () => {
        if(this.state.conversation) {
            this.setState({
                isSaving: true,
            });
            this.props.dispatch(editConversationRequest(this.state.conversation)).then(conversation => {
                this.setState({
                    isSaving: false,
                });
                if(conversation) {
                    displayErrors('success', this.props.intl.formatMessage({
                        id: 'conversationEditSuccessAlert',
                        defaultMessage: 'Conversation updated!',
                    }));
                    this.props.closeForm && this.props.closeForm(conversation);
                } else {
                    displayErrors('error', this.props.intl.formatMessage({
                        id: 'saveRequestError',
                        defaultMessage: 'Error on saving!',
                    }));
                }
            });
        }
    };

    render() {
        const { conversation } = this.state;
        return (
            <div className="p-3" style={{ overflow: 'auto' }}>
                <h6 className="text-primary"><FormattedMessage id="configuration" defaultMessage="Configuration" /></h6>
                <FormGroup>
                    <Toggle
                        id="statusField"
                        name="status"
                        checked={conversation.status === CONVERSATION_STATUS_OPEN || false}
                        onChange={this.handleChangeStatus}
                    />
                    <Label for="statusField" className="ps-2 align-text-bottom"><FormattedMessage id="conversationStatusLabel" defaultMessage="Is this conversation open?" /></Label>
                    <br />
                    <FormText>
                        <FontAwesomeIcon icon="info-circle" /> <FormattedMessage id="conversationStatusHelp" defaultMessage="If conversation is open, participants will be able to chat." />
                    </FormText>
                </FormGroup>

                <FormGroup>
                    {
                        this.props.users && this.props.users.length
                        ? <Fragment>
                                <Label for="participantsField"><FormattedMessage id="conversationParticipantsLabel" defaultMessage="Participants" /></Label>
                                <Autocomplete
                                    getItemValue={item => `${item.firstName || ''} ${item.lastName || ''}`}
                                    items={this.props.users.filter(user => !getConversationParticipantIds(conversation).includes(user._id))}
                                    renderItem={(item, { query, isHighlighted }) => (
                                        <div key={item._id} className={classnames('p-2', isHighlighted && 'bg-light')}>
                                            {`${item.firstName || ''} ${item.lastName || ''}`}
                                        </div>
                                    )}
                                    onSelect={this.handleAddParticipant}
                                    shouldItemRender={(item, value) => !value || (item && ((item.firstName && item.firstName.toLowerCase().includes(value.toLowerCase())) || (item.lastName && item.lastName.toLowerCase().includes(value.toLowerCase())) || (item.email && item.email.toLowerCase().includes(value.toLowerCase()))))}
                                    inputProps={{
                                        id: 'participantsField',
                                        name: 'participants',
                                        className: 'form-control',
                                        placeholder: this.props.intl.formatMessage({ id: 'userFillPlaceholder', defaultMessage: 'Type a user email...' }),
                                    }}
                                />
                          </Fragment>
                        : null
                    }
                    {
                        conversation.participants && conversation.participants.length
                        ? <ListGroup>
                                {conversation.participants.map(participant => <ListGroupItem key={participant._id} className="d-flex justify-content-between">
                                        <span>{`${participant.firstName} ${participant.lastName}`}</span>
                                        {(!conversation.target || getConversationParticipantId(conversation.target.user) !== getConversationParticipantId(participant)) && <Button color="danger" outline size="sm" onClick={() => this.handleRemoveParticipant(participant)}><FontAwesomeIcon icon="trash" /></Button>}
                                                                              </ListGroupItem>)}
                          </ListGroup>
                        : <Alert color="warning" className="text-center">
                                <FontAwesomeIcon icon="exclamation-triangle" /> <FormattedMessage id="conversationNoParticipantsAlert" defaultMessage="No participant in this conversation!" />
                          </Alert>
                    }
                </FormGroup>

                <FormGroup>
                    <Toggle
                        id="optionHideOnListField"
                        name="hideOnList"
                        checked={getConversationOption(conversation, 'hideOnList') || false}
                        onChange={this.handleChangeOption}
                    />
                    <Label for="optionHideOnListField" className="ps-2 align-text-bottom"><FormattedMessage id="conversationOptionHideOnListLabel" defaultMessage="Hide this conversation on participants list?" /></Label>
                    <br />
                    <FormText>
                        <FontAwesomeIcon icon="info-circle" /> <FormattedMessage id="conversationOptionHideOnListHelp" defaultMessage="This conversation will not be displayed on user's conversation list. Used to be only displayed on session pas page." />
                    </FormText>
                </FormGroup>

                <FormGroup className="text-end">
                    <ButtonGroup size="sm">
                        {this.props.closeForm && <Button color="secondary" outline onClick={this.props.closeForm}><FontAwesomeIcon icon="chevron-left" /> <FormattedMessage id="back" defaultMessage="Back" /></Button>}
                        <Button color="success" onClick={this.handleSubmit} disabled={this.state.isSaving}>{this.state.isSaving ? <FontAwesomeIcon icon="spinner" spin /> : <FontAwesomeIcon icon="save" />} <FormattedMessage id="save" defaultMessage="Save" /></Button>
                    </ButtonGroup>
                </FormGroup>
            </div>
        );
    }
}

function mapStateToProps(store, props) {
    return {
        user: getLoggedUser(store),
        users: getUsers(store),
    };
}

ConversationConfig.propTypes = {
    dispatch: PropTypes.func.isRequired,
    intl: PropTypes.object.isRequired,
    user: PropTypes.object,
    users: PropTypes.arrayOf(PropTypes.object),
    conversation: PropTypes.object.isRequired,
    closeForm: PropTypes.func,
};

export default connect(mapStateToProps)(withIntl(ConversationConfig));
